import BoundListenerMgr from './BoundListenerMgr'
export default class EventDispatcher extends EventTarget {

    // constructor
    constructor (args) {
        // call super
        super(args)

        // set extra properties
        this._isSilent = false;
        this._listeners = new Map();
        this._blm = new BoundListenerMgr({
            scope: this
        });
    }


    // methods
    addEventListener(type, listener, options = null) {
        // make sure it exists
        if (!this._listeners.has(type)) this._listeners.set(type, new Set());

        // create shallow copy of options (if it is an object) as we will edit the properties
        options = typeof options === 'object' ? { ...options } : options;

        // define listener scope, extract from options, if not provide use current scope
        let scope = this;
        if (options.scope) {
            scope = options.scope;

            // bind listener
            listener = this._blm.add(getBlmKey(type, listener), listener, scope); // should be fine if listeners are limited to listening and passing down

            // remove scope from options to make sure it is no passed down to Parent
            delete options.scope;
        }

        // get corresponding set & add it for tracking
        const listenerSet = this._listeners.get(type);
        listenerSet.add({
            listener, 
            options: typeof options === 'object' ? options : null,
            useCapture: typeof options === 'boolean' ? options : false
        });

        // call super
        super.addEventListener(type, listener, options);
    }

    removeEventListener(type, listener, options) {
        // get bound listener if it exists instead of the original (remove it instantly)
        listener = this._blm.remove(getBlmKey(type, listener)) || listener;

        // call super
        super.removeEventListener(type, listener, options);

        // remove listener from tracking
        const listenerSet = this._listeners.get(type);
        
        let itemFound = null;
        for (const item of listenerSet) {
            if (item.listener === listener) {
                itemFound = item;
                break;
            }
        }

        if (itemFound) listenerSet.delete(itemFound);
    }


    on (...args) {
        this.addEventListener(...args);
    }

    off (...args) {
        this.removeEventListener(...args);
    }

    getListeners(type = null) {
        return type ? this._listeners.get(type) : this._listeners
    }

    hasListeners(type = null) {
        if (type) {
            const listenerSet = this._listeners.get(type);
            return listenerSet ? listenerSet.size > 0 : false;
        }

        return this._listeners.size > 0;;
    }

    dispatchEvent(eventType, options = {}) {
        // block if instance is supposed to be silent
        if (this._isSilent) return;

        // parse EventType as it can be a string or an Event object
        let event;
        if (eventType instanceof Event) {
            event = eventType;
            eventType = eventType.type;
        }
        else {
            event = new CustomEvent(eventType, options);
        }

        // check if once option was set, if set to true -> remove eventlistener
        const listeners = this._listeners.get(eventType);
        if (listeners) {
            Array.from(listeners)
                .filter(item => item.options && item.options.once)
                .forEach(item => {
                    listeners.delete(item);
                });
        }

        // run super
        super.dispatchEvent(event);
    }

    destroy() {
        // clear map and sets
        for (const [type, listenerSet] of this._listeners.entries()) {
            for (const item of listenerSet) {
                this.removeEventListener(type, item.listener, item.options || item.useCapture);
            }
            listenerSet.clear()
        }
        this._listeners.clear();

    }
    
    toString() {
        return '[object EventDispatcher]';
    }

    // getters & setters
    get isSilent() {
        return this._isSilent;
    }

    set isSilent (value) {
        this._isSilent = value;
    }

    get listeners () {
        return this._listeners;
    }

    get blm () {
        return this._blm;
    }
}

// utils
function getBlmKey(type, listener) {
    return `${type} | ${listener.toString()}`;
}